import {
  PDFViewer,
  Document,
  StyleSheet,
  Page,
  View,
  Text,
  Svg,
  Rect,
  Path,
  Image,
  Font,
} from '@react-pdf/renderer'
import ApiHelper from 'helpers/ApiHelper'
import QRCode from 'qrcode'
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useRouteMatch } from 'react-router-dom'

Font.register({
  family: 'Open Sans',
  fonts: [
    {
      src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf',
    },
    {
      src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-700.ttf',
      fontWeight: 700,
    },
  ],
})

const styles = StyleSheet.create({
  page: {
    backgroundColor: 'white',
    fontFamily: 'Open Sans',
    padding: 0,
    margin: 0,
  },
  body: {
    display: 'flex',
    marginTop: 30,
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  section: {
    marginHorizontal: 0,
    padding: 0,
    flexGrow: 1,
  },
  title: {
    fontSize: 45,
    fontWeight: 700,

    maxWidth: '80%',
    marginHorizontal: 'auto',
  },
  description: {
    fontSize: 25,
    paddingBottom: 20,
    fontWeight: 700,
    maxWidth: '80%',
    marginHorizontal: 'auto',
  },
  qr: {
    width: '100%',
    maxWidth: '70%',
    paddingVertical: 15,
    marginHorizontal: 'auto',
  },
})

export default function AdminQRView() {
  const [qrUri, setQrUri] = useState(null)
  const {
    params: { objectId, status },
  } = useRouteMatch('/objects/:objectId/qr/:status')
  const { data, isLoading, error } = useQuery(
    ['qr-object', objectId],
    () => ApiHelper.showObject(objectId),
    {
      refetchOnWindowFocus: false,
    }
  )

  useEffect(() => {
    const run = async () => {
      if (data?.tags.length > 0) {
        const uri = `https://solver.no/qr/${status}/${data?.tags[0]?.tagId}`
        setQrUri(await generateQR(uri))
      }
    }

    run()
  }, [setQrUri, data?.tags, status])

  if (isLoading || !qrUri)
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <span className="text-sm">Generating...</span>
      </div>
    )
  if (error) return null

  return (
    <div className="mb-5 flex h-full overflow-hidden rounded">
      <PDFViewer width="100%" height="100%">
        <Document>
          <Page size="A4" style={styles.page}>
            <View style={styles.body}>
              <View style={styles.section}>
                <Text style={styles.title}>Velkommen til</Text>
                <Text style={styles.title}>{data.name}</Text>
              </View>

              <View style={styles.qr}>
                <Image
                  style={{
                    padding: 0,
                    margin: 0,
                  }}
                  src={{
                    uri: qrUri,
                  }}
                />
              </View>

              <View style={styles.section}>
                <Text style={styles.description}>Scan QR eller tapp NFC</Text>
              </View>
              <View style={styles.section}>
                <SolverLogo />
              </View>
            </View>
          </Page>
        </Document>
      </PDFViewer>
    </div>
  )
}

async function generateQR(text) {
  try {
    return await QRCode.toDataURL(text, {
      margin: 0,
      padding: 0,
      type: 'image/jpeg',
      quality: 1,
    })
  } catch (err) {
    console.error(err)
  }
}

function SolverLogo() {
  return (
    <Svg
      data-name="Solver logo"
      style={{
        width: 150,
        height: 150,
        overflow: 'hidden',
        borderRadius: '15px',
      }}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 141.73 141.73"
    >
      <Rect width={141.73} height={141.73} rx={30.66} fill="#ee8d1d" />
      <Path
        d="M70.87 122.19c-7.26 0-12-3.84-12-9.79a11.1 11.1 0 012.31-6.35c1.07-1.65 1.67-2.66 1.49-4.23a1.8 1.8 0 00-.86-1.44c-1.1-.71-5-1.94-17.1 1.93a6.27 6.27 0 01-7.86-4.06c-3.28-10.35-3.73-17.16-1.38-20.81a6.33 6.33 0 014.72-2.9c3.28-.39 5.47 1 7.24 2.2a7 7 0 003.87 1.56c4.75 0 5.26-5.2 5.26-7.43s-.51-7.43-5.26-7.44c-1.48 0-2.51.69-3.86 1.57-1.77 1.15-4 2.58-7.24 2.19a6.3 6.3 0 01-4.72-2.9c-2.35-3.65-1.9-10.45 1.38-20.8a6.27 6.27 0 017.86-4.07c12.14 3.87 16 2.64 17.1 1.94a1.82 1.82 0 00.86-1.45c.18-1.57-.42-2.58-1.49-4.23a11.1 11.1 0 01-2.31-6.35c0-5.95 4.72-9.78 12-9.78s11.95 3.83 12 9.78a11 11 0 01-2.31 6.35c-1.07 1.65-1.67 2.66-1.49 4.23a1.82 1.82 0 00.86 1.45c1.1.7 5 1.93 17.1-1.94a6.27 6.27 0 017.86 4.07c3.28 10.35 3.74 17.15 1.38 20.8a6.28 6.28 0 01-4.72 2.9c-3.28.39-5.47-1-7.24-2.19a6.88 6.88 0 00-3.85-1.57c-4.75 0-5.26 5.2-5.26 7.44s.51 7.42 5.26 7.43a6.93 6.93 0 003.85-1.56c1.77-1.15 4-2.59 7.24-2.2a6.31 6.31 0 014.72 2.9c2.36 3.65 1.9 10.46-1.38 20.81a6.27 6.27 0 01-7.9 4.06c-12.14-3.86-16-2.64-17.1-1.93a1.8 1.8 0 00-.86 1.44c-.18 1.57.42 2.58 1.49 4.23a11 11 0 012.31 6.35c-.02 5.95-4.72 9.79-11.97 9.79zM58 95.09a11.46 11.46 0 016.26 1.47 6.3 6.3 0 012.91 4.73c.39 3.27-1 5.47-2.19 7.24-.88 1.34-1.58 2.41-1.57 3.86 0 4.75 5.2 5.26 7.44 5.26s7.42-.51 7.43-5.26c0-1.45-.69-2.51-1.56-3.86-1.16-1.77-2.59-4-2.2-7.24a6.33 6.33 0 012.91-4.73C81.11 94.21 88 94.67 98.38 98a1.67 1.67 0 001.31-.12 1.7 1.7 0 00.85-1c3.82-12 2.59-15.89 1.89-17A1.82 1.82 0 00101 79c-1.57-.18-2.58.42-4.23 1.5a11.2 11.2 0 01-6.33 2.3c-5.94 0-9.78-4.72-9.78-12s3.84-12 9.78-12a11.13 11.13 0 016.33 2.31c1.65 1.07 2.66 1.67 4.23 1.49a1.82 1.82 0 001.44-.86c.7-1.09 1.93-4.93-1.89-17a1.71 1.71 0 00-.85-1 1.67 1.67 0 00-1.31-.12C88 47.06 81.11 47.53 77.45 45.17a6.33 6.33 0 01-2.91-4.73c-.39-3.27 1-5.47 2.2-7.23.87-1.35 1.57-2.42 1.56-3.87 0-4.75-5.2-5.26-7.43-5.26s-7.43.51-7.44 5.26c0 1.45.69 2.52 1.57 3.87 1.15 1.76 2.58 4 2.19 7.23a6.3 6.3 0 01-2.91 4.73c-3.66 2.36-10.51 1.89-20.92-1.43a1.66 1.66 0 00-1.31.12 1.7 1.7 0 00-.86 1c-3.82 12-2.59 15.88-1.89 17a1.82 1.82 0 001.44.86c1.57.18 2.58-.42 4.23-1.49a11.2 11.2 0 016.36-2.31c5.94 0 9.78 4.72 9.78 12s-3.84 11.95-9.78 12A11.2 11.2 0 0145 80.54c-1.65-1.08-2.67-1.68-4.23-1.5a1.82 1.82 0 00-1.44.86c-.7 1.09-1.93 4.93 1.9 17a1.72 1.72 0 002.12 1.1c6.08-1.95 10.95-2.91 14.65-2.91z"
        fill="#fff"
      />
    </Svg>
  )
}
